<script setup>
import { useForm } from '@inertiajs/vue3';
import { ref, watch } from 'vue';
import Select from '@/Components/Form/Select.vue';
import Button from '@/Components/Button.vue';
import GoogleAddress from '@/Components/Form/GoogleAddress.vue';
import Input from '@/Components/Form/Input.vue';
import DatePicker from '@/Components/Form/DatePicker.vue';
import {
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';
import { hasPermission } from '@/Composables/roles.js';

const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    users: {
        type: Array,
        default: () => []
    },
    currentUserId: {
        type: Number,
        default: null
    }
});

const emit = defineEmits(['close', 'success']);

const startStatuses = [
    { id: 'pre', name: 'Vooroplevering' },
    { id: 'end', name: 'Eindoplevering' },
];

const statusOptionsForm = [
    { id: 'self', name: 'Zelfstandig' },
    { id: 'group', name: 'Onzelfstandig' },
];

const usersOptions = props.users.map(user => {
    return {
        id: user.id,
        name: user.name,
    };
});

const newForm = useForm({
    sjht_rel_id: null,
    address: null,
    date: null,
    type: 'self',
    start_status: 'pre',
    user_id: props.currentUserId,
    address_addition: null,
    room_number: null
});

function submitNewForm() {
    newForm.post('/formulieren', {
        preserveScroll: true,
        onSuccess: () => {
            emit('success');
            emit('close');
        }
    });
}

function resetNewForm() {
    newForm.reset();
    newForm.errors = {};
}

function handleClose() {
    emit('close');
}

// Watch for prop changes to reset form when modal closes
watch(() => props.open, (newValue) => {
    if (!newValue) {
        resetNewForm();
    }
});
</script>

<template>
    <TransitionRoot :show="open" as="template" @after-leave="resetNewForm()" appear>
        <Dialog class="relative z-10" @close="handleClose">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed top-0 left-0 right-0 bottom-0 bg-gray-500/25 transition-opacity" />
            </TransitionChild>

            <div class="fixed top-0 left-0 right-0 bottom-0 z-10 w-screen overflow-y-auto p-0 pt-16 sm:p-16 md:p-20">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 scale-95"
                    enter-to="opacity-100 scale-100" leave="ease-in duration-200" leave-from="opacity-100 scale-100"
                    leave-to="opacity-0 scale-95">
                    <DialogPanel
                        class="mx-auto h-full sm:h-auto max-h-full sm:max-w-xl transform rounded-t-xl sm:rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all space-y-3 flex flex-col justify-between">
                        <form @submit.prevent="submitNewForm">
                            <div class="space-y-3 h-full px-3 pt-3 border-b-[1px] mb-4 pb-4">
                                <h2 class="text-lg font-medium text-gray-900 mb-4">Nieuw formulier aanmaken</h2>
                                
                                <Input v-model="newForm.sjht_rel_id" :inputId="'sjht_rel_id'" :label="'Relatienummer'"
                                    :error="newForm.errors.sjht_rel_id" class="w-full" />

                                <GoogleAddress v-model="newForm.address" :inputId="'address'" :label="'Adres'"
                                    :error="newForm.errors.address" class="w-full" />

                                <div class="flex space-x-3">
                                    <Input v-model="newForm.address_addition" :inputId="'address_addition'" :label="'Toevoeging'" 
                                        :error="newForm.errors.address_addition" class="w-1/2" />
                                    <Input v-model="newForm.room_number" :inputId="'room_number'" :label="'Kamernummer'" 
                                        :error="newForm.errors.room_number" class="w-1/2" />
                                </div>

                                <Select v-model="newForm.type" :options="statusOptionsForm" valueName="name"
                                    valueId="id" :label="'Woningtype'" :error="newForm.errors.type" class="w-full" />

                                <Select v-model="newForm.start_status" :options="startStatuses" valueName="name" v-if="hasPermission('house_return_forms_full')"
                                    valueId="id" :label="'Status'" :error="newForm.errors.start_status" class="w-full" />

                                <Select v-model="newForm.user_id" :options="usersOptions" valueName="name" valueId="id" search v-if="hasPermission('house_return_forms_full')"
                                    :label="'Opleveraar'" :error="newForm.errors.user_id" class="w-full" />

                                <DatePicker v-model="newForm.date" :inputId="'date'" :label="'Opleverdatum'"
                                    :error="newForm.errors.date" class="w-full" />
                            </div>
                            <div class="gap-1 flex w-full justify-end px-3 pb-3">
                                <Button class="bg-white hover:bg-gray-100 border-[1px] h-9 w-auto"
                                    @click="handleClose">
                                    <p class="font-semibold text-black">Annuleer</p>
                                </Button>
                                <Button class="bg-hbz hover:bg-hb h-9 w-auto" buttonType="submit">
                                    <p class="font-semibold text-white">Formulier aanmaken</p>
                                </Button>
                            </div>
                        </form>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
